import type { FC } from 'react'
import {
    Alert,
    Anchor,
    Box,
    Card,
    Group,
    Skeleton,
    Stack,
    Text,
    useComputedColorScheme,
} from '@mantine/core'
import { IconAlertTriangle, IconShoppingCart } from '@tabler/icons-react'
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title as ChartTitle,
    Tooltip,
    Legend,
} from 'chart.js'
import type { ChartOptions, TooltipItem } from 'chart.js'
import { Bar } from 'react-chartjs-2'
import { formatDistanceToNow, parseISO } from 'date-fns'
import type { AudienceConfiguration } from '@/lib/domain'
import { formatCompactNumber } from '@/lib/formatNumber'
import {
    chartPalette,
    CHART_GRID_STYLE,
    type ChartPalette,
} from '@/lib/chartColors'
import { nextRefreshAt } from '../api'
import { useAudienceInsights } from '../queries'
import type { AudienceInsightChart } from '../types'

// Registration is global + idempotent; register here so this component stands
// on its own wherever it renders.
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    ChartTitle,
    Tooltip,
    Legend
)

// Per-chart options: hide the legend, format the value axis compactly, and prefer
// the backend's `display_value` in the tooltip when present.
const optionsFor = (
    chart: AudienceInsightChart,
    pal: ChartPalette
): ChartOptions<'bar'> => ({
    // Horizontal bars: categories run down the y-axis, values along the x-axis
    // (so the compact-number formatter lives on x). Reads better for long labels.
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: { display: false },
        tooltip: {
            callbacks: {
                label: (ctx: TooltipItem<'bar'>) => {
                    const bar = chart.bars[ctx.dataIndex]
                    return (
                        bar?.display_value ??
                        formatCompactNumber(bar?.value ?? Number(ctx.parsed.x))
                    )
                },
            },
        },
    },
    scales: {
        x: {
            ticks: {
                color: pal.tick,
                callback: (value) => formatCompactNumber(Number(value)),
            },
            grid: { color: pal.grid },
        },
        y: {
            ticks: { color: pal.tick },
            grid: { color: pal.grid },
        },
    },
})

const dataFor = (chart: AudienceInsightChart, pal: ChartPalette) => ({
    labels: chart.bars.map((b) => b.label),
    datasets: [
        {
            label: chart.title,
            data: chart.bars.map((b) => b.value),
            backgroundColor: pal.bar.fill,
            borderColor: pal.bar.border,
            borderWidth: 1,
        },
    ],
})

export interface AudienceInsightsProps {
    config: AudienceConfiguration
}

// Real "what this audience buys" charts for any segment — transaction,
// user-attribute, or crossed. Each type has its own LLM endpoint (selected in the
// hook) but shares this component, the cache, and the refresh. This is what the
// Makeup tab renders (it replaced an earlier mock-data summary).
export const AudienceInsights: FC<AudienceInsightsProps> = ({ config }) => {
    const { data, isLoading, isError, error, isFetching, regenerate } =
        useAudienceInsights(config)
    // Scheme-aware chart colors (canvas can't read CSS vars, so resolve here);
    // re-renders on the light/dark toggle so the bars + axes recolor.
    const scheme = useComputedColorScheme('light')
    const pal = chartPalette(scheme)

    return (
        <Stack gap='md'>
            <Text size='xs' c='dimmed' tt='uppercase' fw={600} mb='sm'>
                <IconShoppingCart
                    size={14}
                    style={{ verticalAlign: 'text-bottom' }}
                />{' '}
                What this audience buys
            </Text>

            {/* Skeletons during any in-flight fetch — the initial load and a
                regenerate alike — so it's clear the charts are being (re)built. */}
            {isLoading || isFetching ? (
                <Box style={CHART_GRID_STYLE}>
                    <Skeleton height={260} radius='md' />
                    <Skeleton height={260} radius='md' />
                </Box>
            ) : isError ? (
                <Alert
                    color='error'
                    icon={<IconAlertTriangle size={18} />}
                    title="Couldn't load audience insights"
                >
                    {error instanceof Error
                        ? error.message
                        : 'Something went wrong loading these charts.'}
                </Alert>
            ) : !data || data.charts.length === 0 ? (
                <Stack gap='xs' align='flex-start'>
                    <Text size='sm' c='dimmed'>
                        No purchase insights for this segment yet.
                    </Text>
                    <Anchor
                        component='button'
                        type='button'
                        size='sm'
                        onClick={() => void regenerate()}
                    >
                        Retry
                    </Anchor>
                </Stack>
            ) : (
                <>
                    {data.fetchedAt && (
                        <Text size='xs' c='dimmed' mt={-8}>
                            Data run{' '}
                            {formatDistanceToNow(parseISO(data.fetchedAt), {
                                addSuffix: true,
                            })}{' '}
                            · next refresh{' '}
                            {formatDistanceToNow(nextRefreshAt(Date.now()), {
                                addSuffix: true,
                            })}
                        </Text>
                    )}
                    <Box style={CHART_GRID_STYLE}>
                        {data.charts.map((chart) => (
                            <Card
                                key={chart.title}
                                withBorder
                                radius='md'
                                padding='md'
                            >
                                <Text size='sm' fw={600} mb='xs'>
                                    {chart.title}
                                </Text>
                                <Box style={{ height: 260 }}>
                                    <Bar
                                        options={optionsFor(chart, pal)}
                                        data={dataFor(chart, pal)}
                                    />
                                </Box>
                            </Card>
                        ))}
                    </Box>
                    {data.generatedAt && (
                        <Group justify='flex-end'>
                            <Anchor
                                component='button'
                                type='button'
                                size='xs'
                                c='dimmed'
                                onClick={() => void regenerate()}
                            >
                                Insights created by AI{' '}
                                {formatDistanceToNow(
                                    parseISO(data.generatedAt),
                                    { addSuffix: true }
                                )}
                            </Anchor>
                        </Group>
                    )}
                </>
            )}
        </Stack>
    )
}
